import { useQuery } from '@tanstack/react-query'
import {
  createContext,
  use,
  useCallback,
  useState,
  type JSX,
  type ReactNode
} from 'react'

import {
  ALERT_REPORTS_QUERY_KEY,
  fetchAlertReports,
  type AlertRule
} from '../lib/alert-rules-api.ts'
import type { SavedView } from '../lib/saved-views-api.ts'
import { AlertSheet } from './alert-sheet.tsx'
import { QUIET_QUERY_META } from '../lib/query-activity.ts'

const SavedViewAlertContext = createContext<((view: SavedView) => void) | null>(
  null
)

const EditAlertContext = createContext<((rule: AlertRule) => void) | null>(null)

/**
 * Ask the root layout to open an alert from a saved view.
 *
 * Null when the host is absent. The console always mounts the host beside
 * Alert me; a screen rendered on its own, in a test, gets null and does nothing.
 *
 * @returns The opener, or null.
 */
export function useOpenSavedViewAlert(): ((view: SavedView) => void) | null {
  return use(SavedViewAlertContext)
}

/**
 * Ask the root layout to open the alert sheet on a saved alert, to edit it.
 *
 * Null when the host is absent, as {@link useOpenSavedViewAlert} is.
 *
 * @returns The opener, or null.
 */
export function useOpenAlertEdit(): ((rule: AlertRule) => void) | null {
  return use(EditAlertContext)
}

/**
 * SavedViewAlertHost — the alert sheet a saved view starts, or a saved alert
 * reopens to be edited.
 *
 * The alerts page asks for the sheet. It lives here, next to Alert me, so a
 * page never renders one of its own. The catalogue is the same list Alert me
 * reads, and a report that has left it opens nothing.
 *
 * @param props - Host props.
 * @param props.children - The page, and Alert me.
 * @returns The page, and the sheet when a saved view asked for it.
 */
export function SavedViewAlertHost({
  children
}: {
  children: ReactNode
}): JSX.Element {
  const [saved, setSaved] = useState<SavedView | null>(null)
  const [editing, setEditing] = useState<AlertRule | null>(null)
  const catalogue = useQuery({
    queryKey: ALERT_REPORTS_QUERY_KEY,
    meta: QUIET_QUERY_META,
    queryFn: fetchAlertReports,
    staleTime: Infinity,
    retry: false
  })
  const report =
    catalogue.data?.reports.find(item => item.id === saved?.report) ?? null
  const openFromSavedView = useCallback((view: SavedView) => {
    setSaved(view)
  }, [])
  const openEdit = useCallback((rule: AlertRule) => {
    setEditing(rule)
  }, [])
  const watched =
    catalogue.data?.reports.find(item => item.id === editing?.source.report) ??
    null

  return (
    <SavedViewAlertContext value={openFromSavedView}>
      <EditAlertContext value={openEdit}>{children}</EditAlertContext>
      {editing && watched ? (
        <AlertSheet
          report={watched}
          rule={editing}
          open
          onOpenChange={next => {
            if (!next) {
              setEditing(null)
            }
          }}
        />
      ) : null}
      {saved && report ? (
        <AlertSheet
          report={report}
          savedView={saved}
          open
          onOpenChange={next => {
            if (!next) {
              setSaved(null)
            }
          }}
        />
      ) : null}
    </SavedViewAlertContext>
  )
}
